import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { OrderTemplate } from "@shared/schema";

interface TemplateCardGridProps {
  templates: OrderTemplate[];
  isLoading: boolean;
  isError?: boolean;
  onSelectTemplate: (templateId: string) => void;
  disabled?: boolean;
}

export function TemplateCardGrid({
  templates,
  isLoading,
  isError = false,
  onSelectTemplate,
  disabled = false,
}: TemplateCardGridProps) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" role="status">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">{t("components.orders.templateCardGrid.loadingTemplates")}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-muted-foreground py-4" role="alert">
        {t("components.orders.templateCardGrid.failedToLoadTemplates")}
      </p>
    );
  }

  if (templates.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        {t("components.orders.templateCardGrid.noTemplatesYetCreate")}
      </p>
    );
  }

  return (
    <div
      role="list"
      aria-label={t("components.orders.templateCardGrid.orderTemplates")}
      className="flex gap-3 overflow-x-auto pb-2 md:flex-wrap md:overflow-x-visible"
    >
      {templates.map((template) => {
        const itemCount = template.products?.length ?? 0;

        return (
          <Card
            key={template.uid}
            role="listitem"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (!disabled) onSelectTemplate(template.uid);
              }
            }}
            className="w-[160px] flex-shrink-0 transition-colors cursor-pointer hover:border-muted-foreground/30"
          >
            <CardContent className="p-3">
              <div className="mb-2">
                <p className="text-sm font-medium truncate">
                  {template.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {itemCount} {itemCount === 1 ? "item" : "items"}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                disabled={disabled}
                onClick={() => onSelectTemplate(template.uid)}
              >
                {t("components.orders.templateCardGrid.useTemplate")}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
